// DashboardB.js — Vista Institucional del Portafolio (Hidrometrics Pluvia)
//
// v0.3 — correcciones de diseño sobre v0.2, con el código REAL de Topbar.js/
// DashboardA.css como referencia (Fernando los compartió):
//   1. Se QUITÓ la segunda barra azul que había agregado — <Topbar/> YA es
//      una barra azul completa; duplicarla se veía mal (dos navbars
//      apiladas). El header del portafolio ahora es una tarjeta blanca
//      normal, igual que el resto de las secciones.
//   2. Los filtros rápidos de fecha ahora usan EXACTAMENTE el mismo patrón
//      visual que .db-quick-filters/.db-chart-tab de Dashboard A (pastillas
//      claras dentro de una tarjeta blanca) — Dashboard A tampoco pone sus
//      filtros en una barra azul, así que replicar eso era el error.
//   3. Mapa: Leaflet + OpenStreetMap en vez de Google Maps JS API — el
//      "📍 Mapa" de Dashboard A funciona sin key porque es un iframe de
//      Google con output=embed (un solo punto de búsqueda, no soporta N
//      pines dinámicos). Para el portafolio, que sí necesita N pines con
//      zoom adaptativo, Leaflet+OSM da lo mismo sin requerir ninguna key.
//      Requiere agregar la dependencia npm "leaflet" (no estaba en el
//      proyecto): `npm install leaflet`.
//   4. Tabla de instalaciones con DataTable (sin cambios respecto a v0.2).
import React, { useContext, useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { AuthContext } from "../AuthContext/AuthContext";
import Topbar from "../Topbar/Topbar";
import ChatWidget from "../Chat/ChatWidget";
import "./DashboardB.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Centro por default: Acapulco de Juárez, Guerrero.
const ACAPULCO_CENTER = [16.8531, -99.8237];
const ACAPULCO_DEFAULT_ZOOM = 12;

const fmt0 = (v) => Math.round(parseFloat(v) || 0);
const fmtL = (v) => {
  const n = parseFloat(v) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} millones L`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)} mil L`;
  return `${fmt0(n)} L`;
};

const todayYMD = () => {
  const d = new Date(),
    p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const toYMD = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const HYDRAULIC_LABELS = {
  idle: "En espera",
  rain_detected: "Lluvia detectada",
  first_flush: "Separando primeras lluvias",
  first_flush_complete: "Listo para cosechar",
  harvesting: "Cosechando",
  overflow: "Tanque lleno / Rebosando",
  filter_check: "Revisar filtro",
  fault: "Falla de sensor",
  unclassified: "Sin clasificar",
};

const HYDRAULIC_COLORS = {
  idle: "#9aabb8",
  rain_detected: "#3498db",
  first_flush: "#3498db",
  first_flush_complete: "#3498db",
  harvesting: "#27ae60",
  overflow: "#e67e22",
  filter_check: "#e74c3c",
  fault: "#c0392b",
  unclassified: "#c8d5e5",
};

const TYPE_LABEL = { escuela: "Escuela", hotel: "Hotel", otro: "Otro" };

const CONN_LABEL = {
  online: "Conectado",
  unstable: "Intermitente",
  offline: "Sin conexión",
  unknown: "No evaluable",
};

const SEVERITY_COLOR = { critica: "#c0392b", alta: "#d68910" };

/* ── Helpers de estado operativo — mismos que Dashboard A, duplicados
   aquí porque ambos archivos son independientes (sin módulo compartido
   de utilidades todavía). Solo se usan dentro del modal de detalle. ── */
const fmt1 = (v) => (parseFloat(v) || 0).toFixed(1);

const OP_STATES_MODAL = [
  { key: "Dry / Idle", label: "En espera", icon: "⏸" },
  { key: "Rain Detected", label: "Lluvia detectada", icon: "🌧" },
  { key: "First Flush Active", label: "Separando primeras lluvias", icon: "🔀" },
  { key: "First Flush Complete", label: "Listo para cosechar", icon: "✅" },
  { key: "Harvesting Active", label: "Cosechando agua", icon: "💧" },
  { key: "Tank Overflow", label: "Tanque lleno / Rebosando", icon: "🛢" },
  { key: "Filter Check", label: "Revisar filtro", icon: "🔧" },
];
const SPECIAL_STATES_MODAL = {
  "Fault State": { label: "Falla de sensor" },
  "Unclassified / Transition": { label: "Estado transitorio" },
};
const stateLabelModal = (key) =>
  OP_STATES_MODAL.find((s) => s.key === key)?.label ||
  SPECIAL_STATES_MODAL[key]?.label ||
  key ||
  "Sin datos";
const stateColorModal = (opState) => {
  if (!opState) return "#95a5a6";
  if (["Filter Check", "Fault State"].includes(opState)) return "#e74c3c";
  if (opState === "Tank Overflow") return "#e67e22";
  if (opState === "Harvesting Active") return "#27ae60";
  if (["Rain Detected", "First Flush Active", "First Flush Complete"].includes(opState))
    return "#3498db";
  return "#95a5a6";
};

/* ── Modal de detalle de dispositivo — se abre desde un pin del mapa o
   una fila de la tabla. Reutiliza los MISMOS endpoints que ya usa
   Dashboard A (latestKnownReading, dashboard/summary, lastRainEvent) —
   no requiere ningún cambio de backend. A propósito NO incluye las
   gráficas interactivas de Dashboard A, solo información + acumulados,
   como pidió Fernando ("no debe ser igual"). ── */
const DeviceDetailModal = ({ installation, dateRange, onClose }) => {
  const [reading, setReading] = useState(null);
  const [summary, setSummary] = useState(null);
  const [lastRainEvent, setLastRainEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!installation) return;
    setLoading(true);
    const id = installation.id_device;
    const start = dateRange?.start || "2020-01-01";
    const end = dateRange?.end || todayYMD();

    Promise.all([
      fetch(`${API_BASE}/api/dashboard/latestKnownReading?id_device=${id}`)
        .then((r) => r.json())
        .catch(() => ({ success: false })),
      fetch(
        `${API_BASE}/api/dashboard/summary?id_device=${id}&dateStart=${start}&dateEnd=${end}`,
      )
        .then((r) => r.json())
        .catch(() => ({ success: false })),
      fetch(`${API_BASE}/api/dashboard/lastRainEvent?id_device=${id}`)
        .then((r) => r.json())
        .catch(() => ({ success: false })),
    ]).then(([rData, sData, eData]) => {
      setReading(rData.success ? rData.reading : null);
      setSummary(sData.success ? sData.summary : null);
      setLastRainEvent(eData.success ? eData.event : null);
      setLoading(false);
    });
  }, [installation, dateRange]);

  if (!installation) return null;

  const opState = reading?.operational_state || null;
  const tankLevel =
    reading?.tank_level_pct != null ? parseFloat(reading.tank_level_pct) : null;
  const tankVol =
    reading?.tank_volume_l != null ? parseFloat(reading.tank_volume_l) : null;

  return (
    <div className="pf-modal-overlay" onClick={onClose}>
      <div className="pf-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pf-modal-close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>

        <div className="pf-modal-header">
          <div className="pf-modal-title">{installation.name}</div>
          <div className="pf-modal-subtitle">
            {TYPE_LABEL[installation.type] || "Sin perfil"}
            {installation.estado &&
              ` · ${installation.estado}${installation.municipio ? " / " + installation.municipio : ""}`}
          </div>
        </div>

        {loading ? (
          <div className="pf-modal-loading">
            <div className="pf-spinner" />
            <span>Cargando…</span>
          </div>
        ) : (
          <div className="pf-modal-body">
            {/* Tanque + estado actual */}
            <div className="pf-modal-tank-row">
              <div className="pf-modal-tank-box">
                <div className="pf-modal-tank-pct">
                  {tankLevel != null ? `${fmt0(tankLevel)}%` : "—"}
                </div>
                <div className="pf-modal-tank-label">del tanque</div>
              </div>
              <div className="pf-modal-tank-info">
                <div className="pf-modal-tank-vol">
                  {tankVol != null ? `${fmt0(tankVol)} L` : "Sin datos de nivel"}
                </div>
                <div className="pf-modal-state-row">
                  <span
                    className="pf-modal-state-dot"
                    style={{ background: stateColorModal(opState) }}
                  />
                  {stateLabelModal(opState)}
                </div>
                {installation.connectivity && (
                  <span className={`pf-conn-badge pf-conn-badge--${installation.connectivity}`}>
                    {CONN_LABEL[installation.connectivity] || "—"}
                  </span>
                )}
              </div>
            </div>

            {/* Resumen del período (mismo rango activo en el portafolio) */}
            <div className="pf-modal-section-title">
              RESUMEN DEL PERÍODO
              <span className="pf-modal-section-hint">
                {dateRange?.start ? `${dateRange.start} — ${dateRange.end}` : "Desde el inicio"}
              </span>
            </div>
            <div className="pf-modal-summary-grid">
              <div className="pf-modal-summary-item">
                <span className="pf-modal-summary-icon">🌧</span>
                <span className="pf-modal-summary-label">Lluvia</span>
                <span className="pf-modal-summary-val">
                  {fmt0(summary?.rainfall_mm || 0)} mm
                </span>
              </div>
              <div className="pf-modal-summary-item">
                <span className="pf-modal-summary-icon">☁️</span>
                <span className="pf-modal-summary-label">Oferta pluvial</span>
                <span className="pf-modal-summary-val">
                  {fmt0(summary?.offered_rain_volume_l || 0)} L
                </span>
              </div>
              <div className="pf-modal-summary-item">
                <span className="pf-modal-summary-icon">💧</span>
                <span className="pf-modal-summary-label">Agua cosechada</span>
                <span className="pf-modal-summary-val">
                  {fmt0(summary?.rain_captured_l || 0)} L
                </span>
              </div>
              <div className="pf-modal-summary-item">
                <span className="pf-modal-summary-icon">🚿</span>
                <span className="pf-modal-summary-label">Agua aprovechada</span>
                <span className="pf-modal-summary-val">
                  {fmt0(summary?.delivered_water_l || 0)} L
                </span>
              </div>
              <div className="pf-modal-summary-item">
                <span className="pf-modal-summary-icon">📊</span>
                <span className="pf-modal-summary-label">Eficiencia</span>
                <span className="pf-modal-summary-val">
                  {fmt0(summary?.capture_efficiency_pct || 0)}%
                </span>
              </div>
            </div>

            {/* Último evento de lluvia real — mismo endpoint que Dashboard A */}
            <div className="pf-modal-section-title">ÚLTIMO EVENTO DE LLUVIA</div>
            {lastRainEvent ? (
              <div className="pf-modal-lastevent">
                <div className="pf-modal-lastevent-date">
                  📅 {lastRainEvent.date} {lastRainEvent.time || ""}
                </div>
                <div className="pf-modal-lastevent-grid">
                  <span>
                    Lluvia <strong>{fmt0(lastRainEvent.rainfall_mm)} mm</strong>
                  </span>
                  <span>
                    Oferta <strong>{fmt0(lastRainEvent.offered_rain_l)} L</strong>
                  </span>
                  <span>
                    Cosechada <strong>{fmt0(lastRainEvent.rain_captured_l)} L</strong>
                  </span>
                  <span>
                    Aprovechada <strong>{fmt0(lastRainEvent.delivered_water_l)} L</strong>
                  </span>
                </div>
              </div>
            ) : (
              <div className="pf-modal-lastevent-empty">Sin eventos de lluvia registrados</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Mapa de pines — Leaflet + OpenStreetMap, SIN API key ─────────────
   Carga leaflet como módulo normal de npm (no script inyectado — a
   diferencia de Google Maps, Leaflet no necesita cargar nada por fuera).
   Requiere `npm install leaflet` en el proyecto. */
/* ── Gráfica de agua cosechada/aprovechada — mismo diseño que Dashboard
   A: barras SUPERPUESTAS (no apiladas, no lado a lado). Técnica: ancho
   de barra fijo (OVERLAP_BAR_SIZE) + barGap NEGATIVO igual a ese ancho
   en el <ComposedChart> — el offset entre barras consecutivas es
   barSize + barGap = 0, así que "aprovechada" nace en el mismo x que
   "cosechada". Cosechada = relleno sólido. Aprovechada = relleno casi
   transparente + contorno sólido — así cuando aprovechada es más alta
   que cosechada (pasa seguido), su contorno se distingue sin que el
   relleno tape a la de atrás. Leyenda con íconos cuadrados (ChartLegend
   soporta variante "outline" para que Aprovechada coincida con su
   barra). Consume chart_series del backend, que ya viene con la
   cosecha reconciliada por Δ real de tanque, no la suma cruda. */
const OVERLAP_BAR_SIZE = 10;

const ChartLegend = ({ items }) => (
  <div className="pf-chart-legend">
    {items.map((it) => {
      const cls = `pf-chart-legend-dot${it.outline ? " pf-chart-legend-dot--outline" : ""}`;
      const style = it.outline
        ? { background: `${it.color}2e`, borderColor: it.color } // 2e hex ≈ 18% opacidad, igual que la barra
        : { background: it.color, borderColor: it.color };
      return (
        <span key={it.label} className="pf-chart-legend-item">
          <span className={cls} style={style} />
          {it.label}
        </span>
      );
    })}
  </div>
);

const CHART_RES_LABEL = { day: "por día", week: "por semana", month: "por mes" };

const PortfolioVolumeChart = ({ chartSeries }) => {
  const points = chartSeries?.points || [];
  if (!points.length) {
    return <div className="pf-chart-empty">Sin datos en el periodo seleccionado</div>;
  }
  const isDense = points.length > 20;

  return (
    <>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart
          data={points}
          barCategoryGap="12%"
          barGap={-OVERLAP_BAR_SIZE}
          margin={{ top: 6, right: 14, bottom: 4, left: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eaecf4" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={{ stroke: "#dde5f0" }}
            interval={isDense ? "preserveStartEnd" : 0}
          />
          <YAxis
            tickFormatter={(v) => `${v}L`}
            tick={{ fontSize: 10, fill: "#7a8a9e" }}
            tickLine={false}
            axisLine={false}
            width={46}
          />
          <Tooltip
            formatter={(val, name) => [
              `${fmt0(val)} L`,
              name === "cosechada" ? "Cosechada" : "Aprovechada",
            ]}
          />
          <Bar
            dataKey="cosechada"
            name="cosechada"
            barSize={OVERLAP_BAR_SIZE}
            fill="#0064d2"
            fillOpacity={0.6}
            radius={[3, 3, 0, 0]}
          />
          <Bar
            dataKey="aprovechada"
            name="aprovechada"
            barSize={OVERLAP_BAR_SIZE}
            fill="#1abc9c"
            fillOpacity={0.18}
            stroke="#1abc9c"
            strokeWidth={2}
            radius={[3, 3, 0, 0]}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend
        items={[
          { label: "Agua cosechada (L)", color: "#0064d2" },
          { label: "Agua aprovechada (L)", color: "#1abc9c", outline: true },
        ]}
      />
    </>
  );
};

const PortfolioMap = ({ installations, onSelect }) => {
  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [pinCount, setPinCount] = useState(null); // null = aún no evaluado

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      let L;
      try {
        L = (await import("leaflet")).default;
        await import("leaflet/dist/leaflet.css");
      } catch (err) {
        if (!cancelled) {
          setMapError(
            "Falta instalar la dependencia 'leaflet' (npm install leaflet)",
          );
        }
        return;
      }
      if (cancelled || !mapDivRef.current) return;

      // Los íconos default de Leaflet se rompen con bundlers (webpack) si
      // no se apuntan explícitamente a los assets — se usan los del CDN
      // oficial de Leaflet para evitar el problema del ícono roto.
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl:
          "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapDivRef.current).setView(
          ACAPULCO_CENTER,
          ACAPULCO_DEFAULT_ZOOM,
        );
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(mapInstanceRef.current);
        markersLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      }

      markersLayerRef.current.clearLayers();

      // lat/long pueden llegar como number (parseFloat en backend) o, si
      // el backend desplegado es uno viejo sin esta columna, undefined —
      // NaN se descarta explícitamente para no plotear pines fantasma en
      // (0,0) por un dato corrupto.
      const withCoords = installations.filter(
        (i) =>
          typeof i.latitude === "number" &&
          typeof i.longitude === "number" &&
          !Number.isNaN(i.latitude) &&
          !Number.isNaN(i.longitude),
      );
      setPinCount(withCoords.length);

      if (!withCoords.length) {
        mapInstanceRef.current.setView(ACAPULCO_CENTER, ACAPULCO_DEFAULT_ZOOM);
        return;
      }

      const points = [];
      withCoords.forEach((i) => {
        const color = SEVERITY_COLOR[i.severity] || "#0053ae";
        const marker = L.circleMarker([i.latitude, i.longitude], {
          radius: 8,
          color: "#fff",
          weight: 2,
          fillColor: color,
          fillOpacity: 1,
        });
        // Hover: info rápida (nombre + perfil + municipio).
        marker.bindTooltip(
          `<strong>${i.name}</strong><br/>${TYPE_LABEL[i.type] || ""}${i.municipio ? " · " + i.municipio : ""}`,
          { direction: "top", offset: [0, -8] },
        );
        // Clic: abre el modal completo con la info general del sistema.
        marker.on("click", () => onSelect && onSelect(i));
        marker.addTo(markersLayerRef.current);
        points.push([i.latitude, i.longitude]);
      });

      if (points.length > 1) {
        mapInstanceRef.current.fitBounds(points, { padding: [30, 30] });
      } else {
        mapInstanceRef.current.setView(points[0], 15);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [installations]);

  useEffect(
    () => () => {
      // Al desmontar el componente, destruir la instancia del mapa para
      // que un remount (ej. cambio de filtro) no choque con un mapa "ya
      // inicializado" en el mismo contenedor.
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    },
    [],
  );

  if (mapError) {
    return (
      <div className="pf-map-error">
        🗺️ {mapError}
        <div className="pf-map-error-hint">
          El resto del portafolio funciona normalmente.
        </div>
      </div>
    );
  }

  return (
    <div className="pf-map-container">
      <div ref={mapDivRef} className="pf-map-canvas" />
      {/* Aviso visible cuando hay instalaciones pero NINGUNA trae
          coordenadas numéricas — así se distingue de inmediato "mapa
          vacío por falta de datos" de "mapa roto", típicamente porque el
          backend desplegado todavía no manda latitude/longitude. */}
      {pinCount === 0 && installations.length > 0 && (
        <div className="pf-map-nocoords-banner">
          ⚠️ Ninguna instalación trae coordenadas — revisa que el backend
          desplegado sea la versión más reciente.
        </div>
      )}
    </div>
  );
};

/* ── Comparación del portafolio por perfil (escuela/hotel/otro) ──────
   Se calcula ENTERAMENTE en el frontend a partir de `installations`, que
   el backend ya regresa completo — no hace falta ningún endpoint nuevo,
   solo agrupar por tipo lo que ya llegó. */
const PROFILE_META = {
  escuela: { label: "Escuelas", icon: "🏫", color: "#0064d2" },
  hotel: { label: "Hoteles", icon: "🏨", color: "#1abc9c" },
  otro: { label: "Otro", icon: "📍", color: "#8e44ad" },
};

const PortfolioComparison = ({ installations }) => {
  const groups = { escuela: null, hotel: null, otro: null };
  let grandTotalHarvested = 0;

  installations.forEach((i) => {
    const key = PROFILE_META[i.type] ? i.type : "otro";
    if (!groups[key]) {
      groups[key] = { count: 0, harvested: 0, utilized: 0, capacity: 0, area: 0 };
    }
    const g = groups[key];
    g.count++;
    if (i.has_period_data) {
      g.harvested += i.harvested_l || 0;
      g.utilized += i.utilized_l || 0;
    }
    g.capacity += i.capacity_l || 0;
    g.area += i.catchment_area_m2 || 0;
    grandTotalHarvested += i.has_period_data ? i.harvested_l || 0 : 0;
  });

  const activeGroups = Object.entries(groups).filter(([, g]) => g);

  if (!activeGroups.length) {
    return <div className="pf-comparison-empty">Sin perfil asignado en estas instalaciones</div>;
  }

  return (
    <div className="pf-comparison-list">
      {activeGroups.map(([key, g]) => {
        const meta = PROFILE_META[key];
        const pct = grandTotalHarvested > 0 ? Math.round((g.harvested / grandTotalHarvested) * 100) : 0;
        return (
          <div key={key} className="pf-comparison-row">
            <div className="pf-comparison-row-head">
              <span className="pf-comparison-row-label">
                {meta.icon} {meta.label}
              </span>
              <span className="pf-comparison-row-pct">{pct}% del total</span>
            </div>
            <div className="pf-comparison-row-value">{fmtL(g.harvested)}</div>
            <div className="pf-comparison-bar">
              <div
                className="pf-comparison-bar-fill"
                style={{ width: `${pct}%`, background: meta.color }}
              />
            </div>
            <div className="pf-comparison-row-meta">
              {g.count} instalaciones · Aprovechada {fmtL(g.utilized)} · Área{" "}
              {fmt0(g.area)} m² · Cap. {fmt0(g.capacity)} L
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DashboardB = ({ onSwitchDashboard }) => {
  const { user } = useContext(AuthContext);

  const [dateRange, setDateRange] = useState({ start: "", end: todayYMD() });
  const [activeQuickFilter, setActiveQuickFilter] = useState("inicio");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [municipioFilter, setMunicipioFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableFilter, setTableFilter] = useState("");
  const [detailInstallation, setDetailInstallation] = useState(null);

  useEffect(() => {
    document.body.classList.add("home-page");
    return () => document.body.classList.remove("home-page");
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const params = new URLSearchParams({
      id_user: user.id_user,
      user_type: user.type,
    });
    if (dateRange.start) params.set("dateStart", dateRange.start);
    if (dateRange.end) params.set("dateEnd", dateRange.end);
    if (estadoFilter) params.set("estado", estadoFilter);
    if (municipioFilter) params.set("municipio", municipioFilter);
    if (typeFilter) params.set("installation_type", typeFilter);

    fetch(`${API_BASE}/api/portfolio/summary?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setPortfolio(d.portfolio);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, dateRange, estadoFilter, municipioFilter, typeFilter]);

  const applyQuickRange = (type) => {
    setActiveQuickFilter(type);
    const today = new Date();
    const todayStr = todayYMD();
    if (type === "hoy") setDateRange({ start: todayStr, end: todayStr });
    else if (type === "7dias") {
      const s = new Date(today);
      s.setDate(s.getDate() - 6);
      setDateRange({ start: toYMD(s), end: todayStr });
    } else if (type === "30dias") {
      const s = new Date(today);
      s.setDate(s.getDate() - 29);
      setDateRange({ start: toYMD(s), end: todayStr });
    } else if (type === "ano") {
      const s = new Date(today.getFullYear(), 0, 1);
      setDateRange({ start: toYMD(s), end: todayStr });
    } else if (type === "inicio") {
      setDateRange({ start: "", end: todayStr });
    }
  };

  const hero = portfolio?.hero_metrics;
  const health = portfolio?.health;
  const installations = portfolio?.installations || [];

  const estadosDisponibles = [
    ...new Set(installations.map((i) => i.estado).filter(Boolean)),
  ].sort();
  const municipiosDisponibles = [
    ...new Set(installations.map((i) => i.municipio).filter(Boolean)),
  ].sort();


  // ── Columnas de la tabla de instalaciones (DataTable) ────────────────────
  const tableColumns = [
    { name: "Instalación", selector: (row) => row.name, sortable: true, wrap: true },
    {
      name: "Perfil",
      selector: (row) => TYPE_LABEL[row.type] || "—",
      sortable: true,
    },
    {
      name: "Estado / Municipio",
      selector: (row) =>
        `${row.estado || "—"}${row.municipio ? " / " + row.municipio : ""}`,
      sortable: true,
      wrap: true,
    },
    {
      name: "Cosechada",
      selector: (row) => (row.has_period_data ? row.harvested_l : -1),
      cell: (row) => (row.has_period_data ? `${fmt0(row.harvested_l)} L` : "—"),
      sortable: true,
      right: true,
    },
    {
      name: "Aprovechada",
      selector: (row) => (row.has_period_data ? row.utilized_l : -1),
      cell: (row) => (row.has_period_data ? `${fmt0(row.utilized_l)} L` : "—"),
      sortable: true,
      right: true,
    },
    {
      name: "Almacenada",
      selector: (row) => (row.stored_l != null ? row.stored_l : -1),
      cell: (row) =>
        row.stored_l != null ? `${fmt0(row.stored_l)} L (${row.fill_pct}%)` : "Sin dato",
      sortable: true,
      right: true,
    },
    {
      name: "Conectividad",
      selector: (row) => row.connectivity,
      cell: (row) => (
        <span className={`pf-conn-badge pf-conn-badge--${row.connectivity}`}>
          {CONN_LABEL[row.connectivity] || "—"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Estado hidráulico",
      selector: (row) => HYDRAULIC_LABELS[row.hydraulic_state] || "—",
      sortable: true,
      wrap: true,
    },
  ];

  const conditionalRowStyles = [
    { when: (row) => row.severity === "critica", style: { borderLeft: "3px solid #c0392b" } },
    { when: (row) => row.severity === "alta", style: { borderLeft: "3px solid #d68910" } },
  ];

  const filteredInstallations = installations.filter((i) =>
    JSON.stringify(i).toLowerCase().includes(tableFilter.toLowerCase()),
  );

  // v1.1 — grupos de contexto para el Topbar COMPARTIDO (misma barra azul
  // que usa Dashboard A, no una segunda barra). Mismo patrón {id, label,
  // content} que ya usa Topbar internamente para el modo "dispositivo".
  const ctxGroups = [
    {
      id: "scope",
      label: "Alcance",
      content: (
        <span className="tb-scope-val">
          {portfolio?.scope?.is_admin ? "Todo el portafolio" : "Mi portafolio"}
          {" · "}
          {portfolio?.scope?.eligible_installations ?? 0} instalaciones
        </span>
      ),
    },
    {
      id: "period",
      label: "Periodo",
      content: (
        <div className="tb-qf-group">
          {[
            { key: "hoy", label: "Hoy" },
            { key: "7dias", label: "Últ. 7 días" },
            { key: "30dias", label: "Últ. 30 días" },
            { key: "ano", label: "Año" },
            { key: "inicio", label: "Desde el inicio" },
          ].map((opt) => (
            <button
              key={opt.key}
              className={`tb-qf-btn${activeQuickFilter === opt.key ? " tb-qf-btn--active" : ""}`}
              onClick={() => applyQuickRange(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ),
    },
    {
      id: "estado",
      label: "Estado",
      content: (
        <select
          className="tb-ctx-select"
          value={estadoFilter}
          onChange={(e) => setEstadoFilter(e.target.value)}
        >
          <option value="">Todos</option>
          {estadosDisponibles.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      ),
    },
    {
      id: "municipio",
      label: "Municipio",
      content: (
        <select
          className="tb-ctx-select"
          value={municipioFilter}
          onChange={(e) => setMunicipioFilter(e.target.value)}
        >
          <option value="">Todos</option>
          {municipiosDisponibles.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      ),
    },
    {
      id: "perfil",
      label: "Perfil",
      content: (
        <select
          className="tb-ctx-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">Todos</option>
          <option value="escuela">Escuelas</option>
          <option value="hotel">Hoteles</option>
          <option value="otro">Otro</option>
        </select>
      ),
    },
  ];

  return (
    <div className="pf-wrapper">
      {/* Topbar COMPARTIDO — misma barra azul de Dashboard A, ahora
          mostrando el contexto del portafolio en vez de un card aparte. */}
      <Topbar customCtxGroups={ctxGroups} />

      {/* Título — texto simple sobre el fondo de la página, SIN card:
          Topbar ya es la barra de navegación, esto es solo un encabezado
          de sección, no una segunda barra compitiendo visualmente. */}
      <div className="pf-page-title">
        <div>
          <span className="pf-title">VISTA INSTITUCIONAL DEL PORTAFOLIO</span>
          {portfolio?.context?.as_of && (
            <span className="pf-subtitle">
              {" "}
              · corte:{" "}
              {new Date(portfolio.context.as_of).toLocaleString("es-MX", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </span>
          )}
        </div>
        {onSwitchDashboard && (
          <button className="pf-switch-link" onClick={onSwitchDashboard}>
            🔀 Cambiar a Dashboard Operativo
          </button>
        )}
      </div>

      {loading ? (
        <div className="pf-loading">
          <div className="pf-spinner" />
          <span>Cargando portafolio…</span>
        </div>
      ) : !hero ? (
        <div className="pf-empty card">
          Sin instalaciones en el alcance seleccionado para este periodo.
        </div>
      ) : (
        <>
          {/* Banda A — 5 KPIs hero */}
          <div className="pf-hero-grid">
            <div className="pf-hero-card">
              <div className="pf-hero-icon">☁️</div>
              <div className="pf-hero-label">Agua cosechada acumulada</div>
              <div className="pf-hero-value">{fmtL(hero.harvested.value_l)}</div>
              <div className="pf-hero-coverage">
                Cobertura: {hero.harvested.coverage.included}/
                {hero.harvested.coverage.eligible} instalaciones (
                {hero.harvested.coverage.pct}%)
              </div>
            </div>

            <div className="pf-hero-card">
              <div className="pf-hero-icon">🚿</div>
              <div className="pf-hero-label">Agua aprovechada</div>
              <div className="pf-hero-value">{fmtL(hero.utilized.value_l)}</div>
              <div className="pf-hero-coverage">
                {hero.utilized.ratio_to_harvested_pct}% de lo cosechado
              </div>
            </div>

            <div className="pf-hero-card">
              <div className="pf-hero-icon">🏗️</div>
              <div className="pf-hero-label">Infraestructura operativa</div>
              <div className="pf-hero-value">
                {hero.infrastructure.operational}/{hero.infrastructure.funded}
              </div>
              <div className="pf-hero-coverage">
                {hero.infrastructure.commissioned} comisionadas ·{" "}
                {fmt0(hero.infrastructure.catchment_area_m2)} m² de captación
              </div>
            </div>

            <div className="pf-hero-card">
              <div className="pf-hero-icon">💧</div>
              <div className="pf-hero-label">Agua almacenada ahora</div>
              <div className="pf-hero-value">{fmtL(hero.stored.value_l)}</div>
              <div className="pf-hero-coverage">
                {hero.stored.fill_pct}% de llenado · cobertura{" "}
                {hero.stored.coverage_pct}%
              </div>
            </div>

            <div className="pf-hero-card">
              <div className="pf-hero-icon">🏫</div>
              <div className="pf-hero-label">Cobertura institucional</div>
              <div className="pf-hero-value">
                {hero.institutional_coverage.escuela} esc. /{" "}
                {hero.institutional_coverage.hotel} hot.
              </div>
              <div className="pf-hero-coverage">
                {hero.institutional_coverage.municipios} municipios ·{" "}
                {hero.institutional_coverage.estados} estados
              </div>
            </div>

            {/* 6ta tarjeta — Salud del sistema + Conectividad, compacta.
                (Atención ahora vive en su propia 7ma tarjeta — juntar las
                3 cosas alteraba la altura de toda la fila y se veía mal.) */}
            <div className="pf-hero-card pf-hero-card--health">
              <div className="pf-hero-icon">🩺</div>
              <div className="pf-hero-label">Salud del sistema</div>

              <div className="pf-mini-health-bar">
                {Object.entries(health.hydraulic_states).map(([k, v]) => (
                  <div
                    key={k}
                    className="pf-health-seg"
                    style={{ flexGrow: v, background: HYDRAULIC_COLORS[k] || "#c8d5e5" }}
                    title={`${HYDRAULIC_LABELS[k] || k}: ${v}`}
                  />
                ))}
              </div>

              <div className="pf-mini-conn-row">
                <span className="pf-mini-conn-item pf-mini-conn-item--online">
                  🟢 {health.connectivity.online}
                </span>
                <span className="pf-mini-conn-item pf-mini-conn-item--unstable">
                  🟡 {health.connectivity.unstable}
                </span>
                <span className="pf-mini-conn-item pf-mini-conn-item--offline">
                  🔴 {health.connectivity.offline}
                </span>
              </div>
            </div>

            {/* 7ma tarjeta — Requieren atención, con su propio scroll
                interno (no altera el alto de la fila sin importar cuántos
                registros tenga). */}
            <div className="pf-hero-card pf-hero-card--attention">
              <div className="pf-hero-icon">⚠️</div>
              <div className="pf-hero-label">
                Requieren atención
                {health.attention.count > 0 && (
                  <span className="pf-hero-attention-count">{health.attention.count}</span>
                )}
              </div>
              {health.attention.count === 0 ? (
                <div className="pf-attention-ok">Sin atención pendiente</div>
              ) : (
                <ul className="pf-attention-list">
                  {health.attention.items.map((a) => (
                    <li
                      key={a.id_device}
                      className={`pf-attention-item pf-attention-item--${a.severity}`}
                    >
                      <span className="pf-attention-name">{a.name}</span>
                      <span className="pf-attention-reason">{a.reason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Segunda fila — comparación por perfil | gráfica | mapa. */}
          <div className="pf-row2">
            <div className="pf-health-card pf-comparison-card card">
              <div className="pf-health-title">COMPARACIÓN DEL PORTAFOLIO</div>
              <div className="pf-comparison-sub">Escuelas frente a hoteles</div>
              <PortfolioComparison installations={installations} />
            </div>

            <div className="pf-health-card pf-chart-card-b card">
              <div className="pf-health-title-row">
                <span className="pf-health-title">AGUA COSECHADA Y APROVECHADA</span>
                {portfolio?.chart_series?.resolution && (
                  <span className="pf-chart-res-badge">
                    {CHART_RES_LABEL[portfolio.chart_series.resolution]}
                  </span>
                )}
              </div>
              <PortfolioVolumeChart chartSeries={portfolio?.chart_series} />
            </div>

            <div className="pf-health-card pf-map-card card">
              <div className="pf-health-title">MAPA DE INSTALACIONES</div>
              <PortfolioMap installations={installations} onSelect={setDetailInstallation} />
            </div>
          </div>

          {/* Tabla de instalaciones — DataTable paginado */}
          <div className="pf-table-card card">
            <div className="pf-table-header">
              <div className="pf-table-title">INSTALACIONES</div>
              <input
                type="text"
                className="pf-table-search"
                placeholder="Buscar instalación..."
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
              />
            </div>
            <DataTable
              columns={tableColumns}
              data={filteredInstallations}
              pagination
              highlightOnHover
              striped
              pointerOnHover
              onRowClicked={setDetailInstallation}
              conditionalRowStyles={conditionalRowStyles}
              noDataComponent={
                <div className="pf-table-empty">Sin resultados para este filtro.</div>
              }
              paginationComponentOptions={{
                rowsPerPageText: "Registros por página",
                rangeSeparatorText: "de",
              }}
            />
          </div>
        </>
      )}

      {/* Modal de detalle — se abre desde un pin del mapa o una fila de la
          tabla. Usa el mismo rango de fechas activo en el portafolio. */}
      <DeviceDetailModal
        installation={detailInstallation}
        dateRange={dateRange}
        onClose={() => setDetailInstallation(null)}
      />

      {/* Asistente (solo administradores con chat_token; el propio widget se oculta si no aplica) */}
      <ChatWidget />
    </div>
  );
};

export default DashboardB;